import React from 'react';
import {
  MSP_TRAT_ROW_BLANK,
  MSP_TRAT_MIN_SESIONES,
  ensureTablaTratamientosMSPStructure,
  patchTratamientoRow,
  tratamientosConDatos,
} from '../../data/planTratamientoMsp';
import MspReadEmpty from './MspReadEmpty';

function formatFechaDisplay(fecha) {
  if (!fecha) return '—';
  const s = String(fecha).slice(0, 10);
  try {
    const [y, m, d] = s.split('-');
    if (y && m && d) return `${d}/${m}/${y}`;
  } catch {
    /* noop */
  }
  return s;
}

function sessionHasData(row) {
  return Boolean(
    row.fecha ||
      row.diagnostico?.trim() ||
      row.tratamiento?.trim() ||
      row.prescripciones?.trim() ||
      row.codigo?.trim() ||
      row.firma?.trim()
  );
}

function AreaField({ value, onChange, readOnly, placeholder, rows = 3, ariaLabel }) {
  if (readOnly) {
    return <div className="msp-trat-readonly">{value || '—'}</div>;
  }
  return (
    <textarea
      className="msp-trat-textarea"
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={ariaLabel}
    />
  );
}

function TratamientoSesion({ row, index, readOnly, onUpdate, onRemove, canRemove }) {
  const fechaVal = row.fecha ? String(row.fecha).slice(0, 10) : '';
  const filled = sessionHasData(row);

  return (
    <article className={`msp-trat-session${filled ? ' msp-trat-session--filled' : ''}`}>
      <header className="msp-trat-session__hdr">
        <div className="msp-trat-session__hdr-main">
          <span className="msp-trat-session__badge" aria-label={`Sesión ${index + 1}`}>
            <i className="bi bi-calendar2-check" aria-hidden />
            Sesión {index + 1}
          </span>
          <div className="msp-trat-session__fecha">
            <span className="msp-trat-session__fecha-lbl">Fecha</span>
            {readOnly ? (
              <span className="msp-trat-fecha-val">{formatFechaDisplay(fechaVal)}</span>
            ) : (
              <input
                type="date"
                className="msp-trat-date"
                value={fechaVal}
                onChange={(e) => onUpdate({ fecha: e.target.value })}
                aria-label={`Fecha sesión ${index + 1}`}
              />
            )}
          </div>
        </div>
        {!readOnly && canRemove && (
          <button
            type="button"
            className="msp-trat-remove"
            onClick={onRemove}
            title={`Quitar sesión ${index + 1}`}
            aria-label={`Quitar sesión ${index + 1}`}
          >
            <i className="bi bi-trash" aria-hidden />
          </button>
        )}
      </header>

      <div className="msp-trat-session-grid">
        <div className="msp-trat-left">
          <div className="msp-trat-block msp-trat-block--grow">
            <div className="msp-trat-col-head">
              <i className="bi bi-clipboard2-pulse" aria-hidden />
              Diagnósticos y complicaciones
            </div>
            <AreaField
              value={row.diagnostico}
              readOnly={readOnly}
              rows={4}
              placeholder="Diagnóstico y complicaciones de la sesión"
              ariaLabel={`Diagnósticos sesión ${index + 1}`}
              onChange={(v) => onUpdate({ diagnostico: v })}
            />
          </div>
        </div>

        <div className="msp-trat-right">
          <div className="msp-trat-block msp-trat-block--grow">
            <div className="msp-trat-col-head msp-trat-col-head--proc">
              <i className="bi bi-bandaid" aria-hidden />
              Procedimientos
            </div>
            <AreaField
              value={row.tratamiento}
              readOnly={readOnly}
              rows={3}
              placeholder="Tratamiento / procedimientos realizados"
              ariaLabel={`Procedimientos sesión ${index + 1}`}
              onChange={(v) => onUpdate({ tratamiento: v })}
            />
          </div>
          <div className="msp-trat-block msp-trat-block--grow">
            <div className="msp-trat-col-head msp-trat-col-head--rx">
              <i className="bi bi-capsule" aria-hidden />
              Prescripciones
            </div>
            <AreaField
              value={row.prescripciones}
              readOnly={readOnly}
              rows={3}
              placeholder="Medicamentos e indicaciones"
              ariaLabel={`Prescripciones sesión ${index + 1}`}
              onChange={(v) => onUpdate({ prescripciones: v })}
            />
          </div>
          <div className="msp-trat-block">
            <div className="msp-trat-col-head msp-trat-col-head--sign">
              <i className="bi bi-pen" aria-hidden />
              Código y firma
            </div>
            <div className="msp-trat-codigo-firma">
              <div className="msp-trat-cf-row">
                <span className="msp-trat-cf-lbl">Código</span>
                {readOnly ? (
                  <span className="msp-trat-cf-val">{row.codigo || '—'}</span>
                ) : (
                  <input
                    type="text"
                    className="msp-trat-cf-input"
                    value={row.codigo}
                    onChange={(e) => onUpdate({ codigo: e.target.value })}
                    placeholder="Código profesional"
                    aria-label={`Código sesión ${index + 1}`}
                  />
                )}
              </div>
              <div className="msp-trat-cf-row">
                <span className="msp-trat-cf-lbl msp-trat-cf-lbl--sign">Firma</span>
                {readOnly ? (
                  <span className="msp-trat-cf-val">{row.firma || row.codigoFirma || '—'}</span>
                ) : (
                  <input
                    type="text"
                    className="msp-trat-cf-input"
                    value={row.firma}
                    onChange={(e) => onUpdate({ firma: e.target.value })}
                    placeholder="Firma profesional"
                    aria-label={`Firma sesión ${index + 1}`}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

/**
 * Tabla MSP de tratamiento — bloques por sesión (fecha, diagnóstico, procedimientos, prescripciones, código/firma).
 */
export default function PlanTratamientoMspTable({
  rows = [],
  onChange,
  readOnly = false,
  showSectionTitle = true,
}) {
  const filledSessions = tratamientosConDatos(rows);

  if (readOnly) {
    if (filledSessions.length === 0) {
      return (
        <MspReadEmpty
          icon="bi-bandaid"
          message="No hay sesiones de tratamiento registradas."
        />
      );
    }
    return (
      <div className="msp-read-trat-list">
        {filledSessions.map((row, index) => (
          <article key={`trat-read-${index}`} className="msp-read-trat-card">
            <header className="msp-read-trat-card__hdr">
              <span className="msp-read-trat-card__sesion">
                <i className="bi bi-calendar-event" />
                Sesión {index + 1}
              </span>
              <span className="msp-read-trat-card__fecha">{formatFechaDisplay(row.fecha)}</span>
            </header>
            <div className="msp-read-trat-card__grid">
              {row.diagnostico ? (
                <div className="msp-read-trat-field msp-read-trat-field--full">
                  <span className="msp-read-trat-field__lbl">Diagnósticos y complicaciones</span>
                  <p className="msp-read-trat-field__val">{row.diagnostico}</p>
                </div>
              ) : null}
              {row.tratamiento ? (
                <div className="msp-read-trat-field">
                  <span className="msp-read-trat-field__lbl">Procedimientos</span>
                  <p className="msp-read-trat-field__val">{row.tratamiento}</p>
                </div>
              ) : null}
              {row.prescripciones ? (
                <div className="msp-read-trat-field">
                  <span className="msp-read-trat-field__lbl">Prescripciones</span>
                  <p className="msp-read-trat-field__val">{row.prescripciones}</p>
                </div>
              ) : null}
              {(row.codigo || row.firma) && (
                <div className="msp-read-trat-field msp-read-trat-field--codes">
                  {row.codigo ? (
                    <span className="msp-read-tag">
                      <i className="bi bi-upc" /> {row.codigo}
                    </span>
                  ) : null}
                  {row.firma ? (
                    <span className="msp-read-tag">
                      <i className="bi bi-pen" /> {row.firma}
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    );
  }

  const sessions = ensureTablaTratamientosMSPStructure(rows);
  const canRemove = sessions.length > MSP_TRAT_MIN_SESIONES;

  const updateSession = (index, patch) => {
    if (!onChange) return;
    onChange(patchTratamientoRow(sessions, index, patch));
  };

  const removeSession = (index) => {
    if (!onChange || sessions.length <= MSP_TRAT_MIN_SESIONES) return;
    onChange(sessions.filter((_, i) => i !== index));
  };

  const addSession = () => {
    if (!onChange) return;
    onChange([...sessions, { ...MSP_TRAT_ROW_BLANK }]);
  };

  return (
    <div className="msp-trat-panel">
      {showSectionTitle ? (
        <div className="msp-trat-panel__hdr">
          <div className="msp-trat-panel__hdr-text">
            <i className="bi bi-clipboard2-pulse" aria-hidden />
            <div>
              <strong>Plan de tratamiento</strong>
              <p>Una tarjeta por sesión: fecha, diagnóstico, procedimientos, prescripciones y firma.</p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="msp-trat-guide" role="note">
        <span className="msp-trat-guide__chip">
          <i className="bi bi-calendar3" aria-hidden />
          Fecha
        </span>
        <span className="msp-trat-guide__chip">
          <i className="bi bi-clipboard2-pulse" aria-hidden />
          Diagnóstico
        </span>
        <span className="msp-trat-guide__chip">
          <i className="bi bi-bandaid" aria-hidden />
          Procedimientos
        </span>
        <span className="msp-trat-guide__chip">
          <i className="bi bi-capsule" aria-hidden />
          Prescripciones
        </span>
        <span className="msp-trat-guide__chip">
          <i className="bi bi-pen" aria-hidden />
          Código / firma
        </span>
      </div>

      <div className="msp-trat-sessions">
        {sessions.map((row, index) => (
          <TratamientoSesion
            key={`trat-ses-${index}`}
            row={row}
            index={index}
            readOnly={readOnly}
            canRemove={canRemove}
            onUpdate={(patch) => updateSession(index, patch)}
            onRemove={() => removeSession(index)}
          />
        ))}
      </div>

      {!readOnly && (
        <div className="msp-trat-footer">
          <button type="button" className="msp-trat-add-btn" onClick={addSession}>
            <i className="bi bi-plus-lg" aria-hidden />
            Añadir sesión de tratamiento
          </button>
          <p className="msp-trat-leyenda mb-0">
            Complete cada bloque de la sesión. Los campos con borde son editables.
          </p>
        </div>
      )}
    </div>
  );
}
